import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Play, CheckCircle2, XCircle,
  AlertTriangle, ChevronDown, ChevronRight, Server, RefreshCw, Zap,
  FlaskConical,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { LastRunTimeline } from './AdminDiagnosticsTimeline';
import { ScheduleCard } from './AdminDiagnosticsScheduleCard';

/**
 * SuperAdmin → Diagnostics
 *
 * One-click runner for every regression test suite on the platform.
 * Suites are grouped by category (Sales / Purchases / Masters / Inventory
 * / Reports / Admin / etc.). Layout & chrome match the rest of the
 * SuperAdmin panel (dark slate-950 surface, slate-800 dividers,
 * amber accent for primary CTAs).
 */
export function AdminDiagnosticsPage() {
  const { admin, loading: authLoading } = useAdminAuth();
  const [suites, setSuites] = useState([]);
  const [results, setResults] = useState({});
  const [running, setRunning] = useState({});
  const [expanded, setExpanded] = useState({});
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  // Bumped after each run so the timeline strip refetches `last-runs`.
  const [timelineKey, setTimelineKey] = useState(0);

  const role = admin?.role;
  const allowed = role === 'superadmin' || role === 'technical_expert';

  const fetchHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const r = await adminApi.get('/admin/diagnostics/health');
      setHealth(r.data);
    } catch (e) {
      setHealth({ all_ok: false, checks: [], error: e?.response?.data?.detail || 'health probe failed' });
    } finally {
      setHealthLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!admin || !allowed) return;
    let cancel = false;
    (async () => {
      try {
        const r = await adminApi.get('/admin/diagnostics/suites');
        if (!cancel) setSuites(r.data.suites || []);
      } catch (e) {
        if (!cancel) toast.error(e?.response?.data?.detail || 'Failed to load suites');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    fetchHealth();
    return () => { cancel = true; };
  }, [admin, allowed, fetchHealth]);

  const runSuites = useCallback(async (keys) => {
    keys.forEach((k) => setRunning((s) => ({ ...s, [k]: true })));
    try {
      const r = await adminApi.post('/admin/diagnostics/run', {
        suite_keys: keys,
        timeout_seconds: 240,
      });
      const next = { ...results };
      const expandUpdate = {};
      (r.data.results || []).forEach((res) => {
        next[res.suite_key] = res;
        if (res.failed > 0 || res.errors > 0 || res.timed_out) {
          expandUpdate[res.suite_key] = true;
        }
      });
      setResults(next);
      if (Object.keys(expandUpdate).length) {
        setExpanded((e) => ({ ...e, ...expandUpdate }));
      }
      const totalFailed = (r.data.results || []).reduce((a, x) => a + x.failed + x.errors, 0);
      if (totalFailed === 0) toast.success('All tests passed.');
      else toast.error(`${totalFailed} test(s) failed.`);
      // Refresh the timeline strip so its dots reflect the new outcomes.
      setTimelineKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run failed');
    } finally {
      keys.forEach((k) => setRunning((s) => ({ ...s, [k]: false })));
    }
  }, [results]);

  // Group suites by their `category` field for the section layout.
  const grouped = useMemo(() => {
    const order = [
      'Bundles', 'E-Invoicing', 'Sales', 'Purchases', 'Masters',
      'Inventory', 'Reports', 'PDF Engine', 'Licensing & Org',
      'Admin Panel', 'Misc',
    ];
    const map = {};
    for (const s of suites) {
      const k = s.category || 'Misc';
      (map[k] = map[k] || []).push(s);
    }
    const sorted = order.filter((c) => map[c]).map((c) => [c, map[c]]);
    // Append any unknown categories at the end.
    Object.keys(map).forEach((c) => {
      if (!order.includes(c)) sorted.push([c, map[c]]);
    });
    return sorted;
  }, [suites]);

  // Aggregate counts across every run so far for a top-of-page health pill.
  const totals = useMemo(() => {
    const t = { passed: 0, failed: 0, errors: 0, total: 0, runs: 0 };
    Object.values(results).forEach((r) => {
      t.passed += r.passed; t.failed += r.failed; t.errors += r.errors;
      t.total += r.total; t.runs += 1;
    });
    return t;
  }, [results]);

  if (authLoading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;
  if (!allowed) return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/admin" className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800" data-testid="admin-diag-back">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex-1">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-amber-400" /> Diagnostics
            </h1>
            <p className="text-sm text-slate-400">
              One-click runners for every regression test suite — verify system integrity in seconds.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {totals.runs > 0 && (
              <span
                className={`hidden md:inline-flex text-xs font-mono px-2.5 py-1.5 rounded border ${(totals.failed + totals.errors) > 0 ? 'bg-rose-950/40 border-rose-800 text-rose-200' : 'bg-emerald-950/40 border-emerald-800 text-emerald-200'}`}
                data-testid="admin-diag-session-totals"
              >
                {totals.passed}/{totals.total} passed
                {(totals.failed + totals.errors) > 0 && ` • ${totals.failed + totals.errors} failed`}
              </span>
            )}
            <Button
              onClick={() => runSuites(['all_smoke'])}
              disabled={running['all_smoke']}
              className="bg-amber-500 hover:bg-amber-600 text-slate-950"
              data-testid="admin-diag-run-smoke"
            >
              {running['all_smoke'] ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Zap className="w-4 h-4 mr-1.5" />}
              Run smoke bundle
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {loading ? (
          <div className="text-slate-400 flex items-center gap-2 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading suites…</div>
        ) : (
          <>
            <LastRunTimeline suites={suites} refreshKey={timelineKey} />

            <HealthCard health={health} loading={healthLoading} onRefresh={fetchHealth} />

            <ScheduleCard
              suites={suites}
              onRunFinished={() => setTimelineKey((k) => k + 1)}
            />

            {grouped.map(([category, suiteList]) => (
              <section
                key={category}
                className="rounded-xl border border-slate-800 bg-slate-900/50 p-6 space-y-4"
                data-testid={`admin-diag-category-${category.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <span className="w-1.5 h-5 rounded bg-amber-400" />
                      {category}
                    </h2>
                    <p className="text-xs text-slate-400 mt-1">
                      {suiteList.length} suite{suiteList.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => runSuites(suiteList.map((s) => s.key))}
                    disabled={suiteList.some((s) => running[s.key])}
                    className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
                    data-testid={`admin-diag-run-category-${category.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                  >
                    {suiteList.some((s) => running[s.key]) ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
                    Run all in {category}
                  </Button>
                </div>
                <div className="space-y-2">
                  {suiteList.map((s) => (
                    <SuiteRow
                      key={s.key}
                      suite={s}
                      running={running[s.key]}
                      result={results[s.key]}
                      expanded={expanded[s.key]}
                      onToggle={() => setExpanded((e) => ({ ...e, [s.key]: !e[s.key] }))}
                      onRun={() => runSuites([s.key])}
                    />
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </main>
    </div>
  );
}

function HealthCard({ health, loading, onRefresh }) {
  const ok = health?.all_ok;
  return (
    <section
      className="rounded-xl border border-slate-800 bg-slate-900/50 p-6"
      data-testid="admin-diag-health"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${ok ? 'bg-emerald-950/40 border border-emerald-800 text-emerald-300' : 'bg-rose-950/40 border border-rose-800 text-rose-300'}`}>
            <Server className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base font-semibold">Live System Health</h2>
            <p className="text-xs text-slate-400">
              Mongo + critical collections + active GSP — checked in real time.
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={loading} className="text-slate-400 hover:text-slate-100 hover:bg-slate-800" data-testid="admin-diag-health-refresh">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </Button>
      </div>
      {health && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mt-4">
          {(health.checks || []).map((c) => (
            <div
              key={c.name}
              className={`rounded-lg border px-3 py-2 text-xs ${c.ok ? 'border-emerald-800 bg-emerald-950/30 text-emerald-200' : 'border-rose-800 bg-rose-950/30 text-rose-200'}`}
              data-testid={`admin-diag-health-check-${c.name.replace(/[^a-z0-9]/gi, '-')}`}
            >
              <div className="flex items-center gap-1 font-medium">
                {c.ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                {c.name}
              </div>
              {c.error && <div className="text-[10px] mt-0.5 break-words opacity-80">{c.error}</div>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function SuiteRow({ suite, running, result, expanded, onToggle, onRun }) {
  const stateColor = !result
    ? 'border-slate-800 bg-slate-900/30'
    : result.failed + result.errors > 0
      ? 'border-rose-800 bg-rose-950/20'
      : 'border-emerald-800 bg-emerald-950/20';
  return (
    <div
      className={`rounded-lg border ${stateColor} p-4 transition-colors`}
      data-testid={`admin-diag-suite-${suite.key}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-slate-100 text-sm">{suite.title}</h3>
            {(suite.tags || []).map((t) => (
              <span
                key={t}
                className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300"
              >
                {t}
              </span>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-1">{suite.description}</p>
          <p className="text-[10px] text-slate-600 mt-1 font-mono break-all">
            {(suite.paths || []).join(' • ')}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {result && <SuiteSummaryPill result={result} />}
          <Button
            onClick={onRun}
            disabled={running}
            size="sm"
            className="bg-amber-500 hover:bg-amber-600 text-slate-950"
            data-testid={`admin-diag-run-${suite.key}`}
          >
            {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
            Run
          </Button>
        </div>
      </div>

      {result && (
        <div className="mt-3 border-t border-slate-800 pt-3">
          <button
            type="button"
            onClick={onToggle}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-100"
            data-testid={`admin-diag-toggle-${suite.key}`}
          >
            {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            {expanded ? 'Hide' : 'Show'} test breakdown ({result.cases.length})
          </button>
          {result.timed_out && (
            <div className="mt-2 text-xs text-amber-200 bg-amber-950/40 border border-amber-800 rounded px-2 py-1.5 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Run timed out — partial results below.
            </div>
          )}
          {expanded && (
            <div className="mt-3 space-y-1">
              {result.cases.map((c, i) => <CaseRow key={i} c={c} />)}
              {result.cases.length === 0 && (
                <pre className="text-[10px] bg-slate-950 border border-slate-800 text-slate-300 p-3 rounded overflow-x-auto">
                  {result.stdout_tail || '(no output)'}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SuiteSummaryPill({ result }) {
  const fail = result.failed + result.errors;
  if (fail > 0) {
    return (
      <span
        className="text-xs font-mono px-2 py-1 rounded bg-rose-950/40 text-rose-200 border border-rose-800"
        data-testid="admin-diag-pill-fail"
      >
        {result.passed}/{result.total} • {fail} failed • {result.duration_ms}ms
      </span>
    );
  }
  return (
    <span
      className="text-xs font-mono px-2 py-1 rounded bg-emerald-950/40 text-emerald-200 border border-emerald-800"
      data-testid="admin-diag-pill-pass"
    >
      {result.passed}/{result.total} • {result.duration_ms}ms
    </span>
  );
}

function CaseRow({ c }) {
  const colorMap = {
    passed: 'text-emerald-400',
    failed: 'text-rose-400',
    error: 'text-rose-400',
    skipped: 'text-amber-400',
  };
  const Icon = c.outcome === 'passed' ? CheckCircle2 : c.outcome === 'skipped' ? AlertTriangle : XCircle;
  return (
    <div className="text-xs flex items-start gap-2 py-1 border-b border-slate-800/50 last:border-b-0">
      <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${colorMap[c.outcome] || 'text-slate-500'}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-slate-200 break-all">{c.name}</span>
          <span className="text-[10px] text-slate-600">{c.duration_ms}ms</span>
        </div>
        {c.message && (
          <pre className="mt-1 text-[10px] bg-slate-950 border border-rose-900/40 text-rose-300 p-2 rounded whitespace-pre-wrap break-all">
            {c.message}
          </pre>
        )}
      </div>
    </div>
  );
}

export default AdminDiagnosticsPage;
